/**
 * What the desktop app reports about an agent it runs, through the same telemetry the MCP and the
 * hooks use (docs/event-protocol.md). The app runs the agent and reads its stream, so it reports the
 * prompt, each tool, the reply, the usage and the files changed itself — the observability does not
 * depend on the agent remembering to call the MCP (docs/refactor/01-rodar-aqui.md).
 *
 * The same rules as the hooks (mcp/hooks/prompt-submitted.mjs):
 *  - a prompt's or a reply's text leaves the machine only when the organization keeps them
 *    (`GET /telemetry/policy`, remembered for a few minutes, "no" when unknown), secrets masked first;
 *  - otherwise only lengths;
 *  - a tool is its name and a short target, masked; never what it read or printed.
 *
 * A report never gets in the way of the run: it is sent in the background and batched. When the API cannot take
 * it (down, overloaded, no internet), it is kept for later — in a JSON file of this account (docs/refactor/52) — and
 * sent again every two minutes and when the internet comes back. A run whose prompt did not get through is kept
 * whole, the prompt first, so its session opens when it is finally sent. A "no" from the server is not kept.
 */

export interface ReporterDeps {
  apiBase: string
  fetch: typeof fetch
  /** This person's Vephus agent key (from the vault), or null when there is none. */
  key: () => Promise<string | null>
  now?: () => Date
  /** How long to gather events before sending them together. */
  batchMs?: number
  /** Where what could not be sent waits (the desktop's outbox file); without it, it is dropped as before. */
  outbox?: OutboxStore
}

/** The file that keeps what could not be sent: the page reads and writes it whole (desktop/src-tauri/src/outbox.rs). */
export interface OutboxStore {
  load(): Promise<string>
  save(content: string): Promise<void>
}

/**
 * Events kept for later. A group with an `opener` is a run whose prompt did not get through: the prompt opens the
 * session when sent, and the events go to it. Without one, they already know their session.
 */
export interface OutboxGroup {
  id: string
  opener: Envelope | null
  events: Envelope[]
  /** When the first of them failed. */
  since: string
}

/** How often what is kept is tried again (the owner's two minutes). */
export const OUTBOX_RETRY_MS = 120_000
/** The most kept: a long outage drops the oldest first rather than grow the file without end. */
export const OUTBOX_MAX_EVENTS = 5000

type Delivery = { kind: 'ok'; body: unknown } | { kind: 'retry' } | { kind: 'drop' }

export interface Policy {
  keepPrompts: boolean
  keepReplies: boolean
  /** The full log level (a paid plan's choice): what the agent says on the way, and each command whole. */
  fullLog?: boolean
}

const MASK = '[redacted]'
const POLICY_TTL_MS = 5 * 60_000
const MAX_TEXT = 8000
const MAX_TARGET = 140
const MAX_COMMAND = 4000
const MAX_BATCH = 50

/** Secrets, by kind: the families the hooks and the server mask. Built from parts, so nothing here looks like a key. */
const SECRET_RULES: RegExp[] = [
  /\b(?:AKIA|ASIA)[0-9A-Z]{16}\b/g,
  new RegExp('-----BEGIN[^-]{0,40}PRIVATE KEY-----[\\s\\S]*?-----END[^-]{0,40}PRIVATE KEY-----', 'g'),
  /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g,
  /\b[Bb]earer\s+[A-Za-z0-9._\-~+/]{16,}=*/g,
  /\b(?:gh[pousr]_[A-Za-z0-9]{16,}|sk-[A-Za-z0-9_-]{16,}|xox[abprs]-[A-Za-z0-9-]{10,}|(?:sk|pk|rk)_(?:live|test)_[A-Za-z0-9]{10,}|dpk_[A-Za-z0-9_-]{16,}|AIza[0-9A-Za-z_-]{35}|glpat-[A-Za-z0-9_-]{20,}|hf_[A-Za-z0-9]{30,}|npm_[A-Za-z0-9]{36}|SG\.[A-Za-z0-9_-]{16,}\.[A-Za-z0-9_-]{16,}|\d{8,10}:AA[A-Za-z0-9_-]{33}|hooks\.slack\.com\/services\/[A-Za-z0-9/]{20,})/g,
  /(?<![A-Za-z0-9-])(?:--?(?:pass(?:word|wd)?|secret|token|api[_-]?key|access[_-]?key|secret[_-]?key|private[_-]?key|client[_-]?secret|auth[_-]?token)(?![A-Za-z0-9_-])\s+["']?[^\s"']{4,}["']?|(?:-u|--user)\s+["']?[^\s:"']+:[^\s"']{3,}["']?)|\bauthorization["']?\s*:\s*["']?basic\s+[A-Za-z0-9+/]{8,}={0,2}/gi,
  /(?<![A-Za-z0-9])(?:pass(?:word|wd)?|secret|token|api[_-]?key|access[_-]?key|secret[_-]?key|private[_-]?key|credential|auth)(?![A-Za-z0-9])["']?\s*[:=]\s*["']?[^\s"',;}]{6,}["']?/gi,
  /\b[a-z][a-z0-9+.-]*:\/\/[^\s:/@]+:[^\s@/]+@/gi,
]

export function redact(text: string): string {
  return SECRET_RULES.reduce((result, rule) => result.replace(rule, MASK), text)
}

/** A short, masked, one-line target for a tool: the file, the start of the command, the pattern. */
export function shortTarget(detail: string): string {
  const line = redact(detail.replace(/\s+/g, ' ').trim())
  return line.length > MAX_TARGET ? `${line.slice(0, MAX_TARGET - 1)}…` : line
}

export function words(text: string): number {
  const trimmed = text.trim()
  return trimmed ? trimmed.split(/\s+/).length : 0
}

export interface Envelope {
  eventId: string
  workSessionId?: string
  eventType: string
  provider: string
  timestamp: string
  schemaVersion: 1
  payload: Record<string, unknown>
}

export class AgentReporter {
  private policyCache: { value: Policy; at: number } | null = null
  private pending: Envelope[] = []
  private timer: ReturnType<typeof setTimeout> | null = null
  private kept: OutboxGroup[] = []
  private loaded: Promise<void> | null = null
  private saveTimer: ReturnType<typeof setTimeout> | null = null
  private retrying: Promise<{ sent: number; left: number }> | null = null
  private readonly listeners = new Set<() => void>()

  constructor(private readonly deps: ReporterDeps) {}

  /** Reads what an earlier run of the app left, tries it at once, and then every two minutes and when back online. */
  start(): () => void {
    this.loaded ??= this.load()
    void this.loaded.then(() => this.retry())
    const every = setInterval(() => void this.retry(), OUTBOX_RETRY_MS)
    const online = () => void this.retry()
    if (typeof window !== 'undefined') window.addEventListener('online', online)
    return () => {
      clearInterval(every)
      if (typeof window !== 'undefined') window.removeEventListener('online', online)
    }
  }

  private async load(): Promise<void> {
    if (!this.deps.outbox) return
    try {
      const text = (await this.deps.outbox.load()).trim()
      const groups = text ? (JSON.parse(text) as OutboxGroup[]) : []
      if (Array.isArray(groups)) this.kept = [...groups.filter((g) => g && Array.isArray(g.events)), ...this.kept]
    } catch {
      // A file that cannot be read is not worth stopping for: what is in memory still goes.
    }
    this.changed()
  }

  /** How many events wait to be sent, and since when. */
  outbox(): { count: number; since: string | null } {
    const count = this.kept.reduce((sum, g) => sum + g.events.length + (g.opener ? 1 : 0), 0)
    return { count, since: this.kept[0]?.since ?? null }
  }

  /** Called whenever what waits changes. Returns how to stop listening. */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private changed() {
    for (const listener of this.listeners) listener()
    if (!this.deps.outbox) return
    if (this.saveTimer) clearTimeout(this.saveTimer)
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null
      void this.deps.outbox!.save(this.kept.length > 0 ? JSON.stringify(this.kept) : '').catch(() => {})
    }, 1000)
  }

  /** Keeps a group for later, the oldest leaving first past the ceiling. Heartbeats are not kept: late, they say nothing. */
  private keep(group: OutboxGroup) {
    if (!this.deps.outbox) return
    group.events = group.events.filter((e) => e.eventType !== 'agent.heartbeat')
    if (!group.opener && group.events.length === 0) return
    this.kept.push(group)
    let over = this.outbox().count - OUTBOX_MAX_EVENTS
    while (over > 0 && this.kept.length > 0) {
      const oldest = this.kept[0]!
      const dropped = Math.min(over, oldest.events.length)
      oldest.events.splice(0, dropped)
      over -= dropped
      if (oldest.events.length === 0) {
        if (oldest.opener) over -= 1
        this.kept.shift()
      }
    }
    this.changed()
  }

  /** An event of a run whose prompt is kept: it waits with it. */
  defer(groupId: string, eventType: string, payload: Record<string, unknown>, provider: string) {
    if (eventType === 'agent.heartbeat') return
    const group = this.kept.find((g) => g.id === groupId)
    if (!group) return
    group.events.push(this.envelope(eventType, payload, provider))
    this.changed()
  }

  /** One call to the API, told apart: sent, worth trying again later (down, overloaded, slow), or refused for good. */
  private async post(path: string, body: unknown): Promise<Delivery> {
    const headers = await this.headers()
    if (!headers) return { kind: 'drop' }
    try {
      const response = await this.deps.fetch(`${this.deps.apiBase}${path}`, { method: 'POST', headers, body: JSON.stringify(body) })
      if (response.ok) return { kind: 'ok', body: await response.json().catch(() => null) }
      return response.status === 408 || response.status === 429 || response.status >= 500 ? { kind: 'retry' } : { kind: 'drop' }
    } catch {
      return { kind: 'retry' }
    }
  }

  /**
   * Sends what is kept, oldest first, and stops at the first sign the API still cannot take it. Never runs twice at
   * once: "Enviar agora" and the timer share one attempt.
   */
  retry(): Promise<{ sent: number; left: number }> {
    this.retrying ??= this.sendKept().finally(() => {
      this.retrying = null
    })
    return this.retrying
  }

  private async sendKept(): Promise<{ sent: number; left: number }> {
    if (this.loaded) await this.loaded
    let sent = 0
    while (this.kept.length > 0) {
      const group = this.kept[0]!
      if (group.opener) {
        const result = await this.post('/telemetry/events', group.opener)
        if (result.kind === 'retry') break
        sent += 1
        if (result.kind === 'drop') {
          this.kept.shift()
          this.changed()
          continue
        }
        const sessionId = (result.body as { workSessionId?: string | null } | null)?.workSessionId ?? null
        group.opener = null
        for (const event of group.events) if (!event.workSessionId && sessionId) event.workSessionId = sessionId
        this.changed()
      }
      let stopped = false
      while (group.events.length > 0) {
        const chunk = group.events.slice(0, MAX_BATCH)
        const result = await this.post('/telemetry/events/batch', { events: chunk })
        if (result.kind === 'retry') {
          stopped = true
          break
        }
        group.events.splice(0, chunk.length)
        sent += chunk.length
        this.changed()
      }
      if (stopped) break
      this.kept.shift()
      this.changed()
    }
    return { sent, left: this.outbox().count }
  }

  private now(): Date {
    return this.deps.now?.() ?? new Date()
  }

  private async headers(): Promise<Record<string, string> | null> {
    const key = await this.deps.key().catch(() => null)
    return key ? { 'Content-Type': 'application/json', 'X-DevPulse-Key': key } : null
  }

  /** What may leave the machine, asked once every few minutes; "nothing" when it cannot be asked. */
  async policy(): Promise<Policy> {
    const cached = this.policyCache
    if (cached && this.now().getTime() - cached.at < POLICY_TTL_MS) return cached.value
    let value: Policy = { keepPrompts: false, keepReplies: false, fullLog: false }
    const headers = await this.headers()
    if (headers) {
      try {
        const response = await this.deps.fetch(`${this.deps.apiBase}/telemetry/policy`, { headers })
        if (response.ok) {
          const body = (await response.json()) as { collectPromptText?: boolean; collectAgentReplies?: boolean; fullLog?: boolean }
          value = { keepPrompts: body.collectPromptText === true, keepReplies: body.collectAgentReplies === true, fullLog: body.fullLog === true }
        }
      } catch {
        // Unknown means no.
      }
    }
    this.policyCache = { value, at: this.now().getTime() }
    return value
  }

  private envelope(eventType: string, payload: Record<string, unknown>, provider: string, workSessionId?: string): Envelope {
    return {
      eventId: crypto.randomUUID(),
      ...(workSessionId ? { workSessionId } : {}),
      eventType,
      provider,
      timestamp: this.now().toISOString(),
      schemaVersion: 1,
      payload,
    }
  }

  /**
   * Sends one event now and returns the session the server put it in — how the first prompt of a
   * conversation learns its session id. Null when it could not be sent (it is kept for later, alone).
   */
  async sendNow(eventType: string, payload: Record<string, unknown>, provider: string, workSessionId?: string): Promise<string | null> {
    return (await this.open(eventType, payload, provider, workSessionId)).sessionId
  }

  /**
   * Sends the event that opens a run. When the API cannot take it now, it is kept as the head of a group, and the
   * run's next events wait with it (`defer` with the group returned): the whole run arrives later, in its session.
   */
  async open(eventType: string, payload: Record<string, unknown>, provider: string, workSessionId?: string): Promise<{ sessionId: string | null; deferred: string | null }> {
    const envelope = this.envelope(eventType, payload, provider, workSessionId)
    const result = await this.post('/telemetry/events', envelope)
    if (result.kind === 'ok') return { sessionId: (result.body as { workSessionId?: string | null } | null)?.workSessionId ?? null, deferred: null }
    if (result.kind === 'drop' || !this.deps.outbox) return { sessionId: null, deferred: null }
    const id = crypto.randomUUID()
    this.keep({ id, opener: envelope, events: [], since: this.now().toISOString() })
    return { sessionId: null, deferred: id }
  }

  /** Queues an event for the next batch (a few hundred milliseconds): tools come in bursts. */
  queue(eventType: string, payload: Record<string, unknown>, provider: string, workSessionId: string) {
    this.pending.push(this.envelope(eventType, payload, provider, workSessionId))
    if (this.pending.length >= MAX_BATCH) {
      void this.flush()
      return
    }
    if (!this.timer) this.timer = setTimeout(() => void this.flush(), this.deps.batchMs ?? 700)
  }

  /** Sends whatever is queued. */
  async flush(): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    const events = this.pending.splice(0, MAX_BATCH)
    if (events.length === 0) return
    // Something already waits: these go behind it, in order, so a session never gets its events before its prompt.
    const result = this.kept.length > 0 ? ({ kind: 'retry' } as const) : await this.post('/telemetry/events/batch', { events })
    if (result.kind === 'retry') this.keep({ id: crypto.randomUUID(), opener: null, events, since: this.now().toISOString() })
    if (this.pending.length > 0) await this.flush()
  }

  /** A prompt's payload: its length, and its text only when the organization keeps prompts. */
  async promptPayload(text: string, extra: Record<string, unknown>): Promise<Record<string, unknown>> {
    const { keepPrompts } = await this.policy()
    return {
      chars: text.length,
      words: words(text),
      ...extra,
      ...(keepPrompts ? { text: redact(text).slice(0, MAX_TEXT) } : {}),
    }
  }

  /** A reply's payload: its length, and its text only when the organization keeps replies. */
  async replyPayload(text: string, clientSessionId: string): Promise<Record<string, unknown>> {
    const { keepReplies } = await this.policy()
    return { chars: text.length, clientSessionId, ...(keepReplies ? { text: redact(text).slice(0, MAX_TEXT) } : {}) }
  }
}

type Payload = Record<string, unknown> | (() => Record<string, unknown> | null)

/** How often a run with nothing else to report says it is alive. */
export const HEARTBEAT_MS = 60_000

/**
 * One run of an agent, as Vephus hears it: the prompt when it starts (which opens or joins the
 * conversation's session), each tool as it is used, a sign of life while it thinks, and at the end the
 * reply, the tokens and the files the run changed. Everything goes in the background.
 */
export class RunReport {
  private sessionId: string | null = null
  /** The kept group this run's events wait in, when its prompt could not be sent (docs/refactor/52). */
  private deferred: string | null = null
  private opened: Promise<string | null> = Promise.resolve(null)
  private early: Array<{ type: string; payload: Payload }> = []
  /** The full log level, known once the policy is read when the run starts. */
  private full = false
  private modelName: string | null = null
  private tokens: { input: number; output: number; cached: number } | null = null
  private beat: ReturnType<typeof setInterval> | null = null

  constructor(
    private readonly reporter: AgentReporter,
    private readonly provider: string,
    private readonly repository: string,
    private readonly clientSessionId: string | null,
  ) {}

  /** A payload, or one decided when the session opens (by then the log level is known); null sends nothing. */
  private emit(type: string, payload: Payload) {
    if (this.sessionId || this.deferred) this.send(type, payload)
    else this.early.push({ type, payload })
  }

  private send(type: string, payload: Payload) {
    const body = typeof payload === 'function' ? payload() : payload
    if (!body) return
    if (this.sessionId) this.reporter.queue(type, body, this.provider, this.sessionId)
    else if (this.deferred) this.reporter.defer(this.deferred, type, body, this.provider)
  }

  /** The prompt: sent at once, so the session exists before the first tool. */
  started(prompt: string, branch: string | null): Promise<string | null> {
    this.opened = (async () => {
      const payload = await this.reporter.promptPayload(prompt, {
        repository: this.repository,
        ...(branch ? { branch } : {}),
        ...(this.clientSessionId ? { clientSessionId: this.clientSessionId } : {}),
      })
      this.full = (await this.reporter.policy()).fullLog === true
      const opened = await this.reporter.open('prompt.submitted', payload, this.provider)
      this.sessionId = opened.sessionId
      this.deferred = opened.deferred
      if (this.sessionId || this.deferred) {
        for (const event of this.early.splice(0)) this.send(event.type, event.payload)
      } else {
        this.early = []
      }
      return this.sessionId
    })()
    this.beat = setInterval(() => this.emit('agent.heartbeat', { repository: this.repository, ...(this.clientSessionId ? { clientSessionId: this.clientSessionId } : {}) }), HEARTBEAT_MS)
    return this.opened
  }

  model(name: string | null) {
    if (name) this.modelName = name
  }

  /** A tool the agent used; on the full log level, the command whole (masked), not its start. */
  tool(name: string, detail: string, command?: string) {
    this.emit('agent.activity', () => ({
      tool: name.slice(0, 60),
      ...(this.full && (command || detail) ? { target: redact((command || detail).trim()).slice(0, MAX_COMMAND) } : detail ? { target: shortTarget(detail) } : {}),
    }))
  }

  /** What the agent said on the way, before its answer: sent, masked, only on the full log level. */
  said(text: string) {
    if (!text.trim()) return
    this.emit('agent.said', () =>
      this.full ? { text: redact(text).slice(0, MAX_TEXT), chars: text.length, ...(this.clientSessionId ? { clientSessionId: this.clientSessionId } : {}) } : null,
    )
  }

  usage(tokens: { input: number; output: number; cached: number } | null) {
    if (tokens) this.tokens = tokens
  }

  /** The end of the run: the reply, the tokens, the files. Returns the session it went to. */
  async ended(reply: string, files: string[], lines: { added: number; deleted: number } | null = null): Promise<string | null> {
    if (this.beat) clearInterval(this.beat)
    this.beat = null
    await this.opened
    if (this.tokens) {
      this.emit('ai.usage', {
        ...(this.modelName ? { model: this.modelName } : {}),
        inputTokens: this.tokens.input,
        outputTokens: this.tokens.output,
        cachedInputTokens: this.tokens.cached,
      })
    }
    if (files.length > 0)
      this.emit('code.changed', {
        filesChanged: files.length,
        ...(lines ? { linesAdded: lines.added, linesDeleted: lines.deleted } : {}),
        files: files.slice(0, 200),
        repository: this.repository,
      })
    if (this.clientSessionId) this.emit('agent.replied', await this.reporter.replyPayload(reply, this.clientSessionId))
    await this.reporter.flush()
    return this.sessionId
  }
}
